import type { EditableField } from '@/components/SignDocumentCommon/types'

export interface PdfTextItem {
  str: string
  transform: number[]
  width: number
  height: number
}

export const isPdfTextItem = (item: unknown): item is PdfTextItem => {
  if (!item || typeof item !== 'object') return false

  const { str, transform, width, height } = item as Partial<PdfTextItem>

  return (
    typeof str === 'string' &&
    typeof width === 'number' &&
    typeof height === 'number' &&
    Array.isArray(transform) &&
    transform.length >= 6 &&
    transform.every((n) => typeof n === 'number' && Number.isFinite(n))
  )
}

const strip = (value: string): string => value.replace(/\s+/g, '').toLowerCase()

const findPlaceholderAnchor = (
  textItems: PdfTextItem[],
  placeholderText: string
): PdfTextItem | undefined => {
  const target = strip(placeholderText)
  if (!target) return undefined

  const stripped = textItems.map((item) => strip(item.str))
  const starts: number[] = []
  let concat = ''
  for (const s of stripped) {
    starts.push(concat.length)
    concat += s
  }

  const at = concat.indexOf(target)
  if (at < 0) return undefined

  for (let i = 0; i < textItems.length; i++) {
    if (stripped[i].length && at >= starts[i] && at < starts[i] + stripped[i].length) {
      return textItems[i]
    }
  }

  return undefined
}

export const resolveFieldsForPage = (
  fields: EditableField[],
  textItems: PdfTextItem[],
  pageNumber: number,
  pageHeightPt: number
): EditableField[] => {
  const resolved: EditableField[] = []

  for (const field of fields) {
    const { location } = field

    if (!location.relative) {
      if (
        location.page === pageNumber &&
        location.top !== undefined &&
        location.left !== undefined
      ) {
        resolved.push(field)
      }
      continue
    }

    if (!location.placeholderText) continue

    const match = findPlaceholderAnchor(textItems, location.placeholderText)
    if (!match) continue

    const anchorLeft = match.transform[4]
    const anchorTop = pageHeightPt - match.transform[5] - match.height / 2

    resolved.push({
      ...field,
      location: {
        page: pageNumber,
        left: anchorLeft + (location.left ?? 0),
        top: anchorTop,
      },
    })
  }

  return resolved
}
